import React from "react";

export interface IAdminColumn<T> {
    header: React.ReactNode;
    render: (row: T, index: number) => React.ReactNode;
    className?: string;
    // papel da coluna no card do mobile: título, canto superior direito, campo rotulado (padrão) ou oculta
    mobile?: "title" | "aside" | "field" | "hidden";
    // oculta a coluna também na tabela do desktop (continua disponível no card do mobile)
    desktopHidden?: boolean;
}

// tabela no desktop (md+) e lista de cards no mobile, a partir da mesma definição de colunas
export default function AdminTable<T>({
    rows,
    columns,
    rowKey,
    actions,
    actionsHeader = ""
}: {
    rows: T[];
    columns: IAdminColumn<T>[];
    rowKey: (row: T, index: number) => string | number;
    actions?: (row: T, index: number) => React.ReactNode;
    actionsHeader?: string;
}) {

    const titleColumn = columns.find(c => c.mobile === "title") || columns[0];
    const asideColumn = columns.find(c => c.mobile === "aside");
    const fieldColumns = columns.filter(c => c !== titleColumn && c !== asideColumn && c.mobile !== "hidden");
    const desktopColumns = columns.filter(c => !c.desktopHidden);

    return (
        <div>
            {/* DESKTOP */}
            <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            {
                                desktopColumns.map((c, i) => (
                                    <th key={i} className={`px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap ${c.className || ""}`}>{c.header}</th>
                                ))
                            }
                            {
                                actions ?
                                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">{actionsHeader}</th>
                                    : null
                            }
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {
                            rows.map((row, idx) => (
                                <tr key={rowKey(row, idx)} className="hover:bg-gray-50/70 transition-colors">
                                    {
                                        desktopColumns.map((c, i) => (
                                            <td key={i} className={`px-4 py-3 align-middle text-gray-800 ${c.className || ""}`}>{c.render(row, idx)}</td>
                                        ))
                                    }
                                    {
                                        actions ?
                                            <td className="px-4 py-3 align-middle">
                                                <div className="flex items-center justify-end gap-2">{actions(row, idx)}</div>
                                            </td>
                                            : null
                                    }
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>

            {/* MOBILE */}
            <ul className="md:hidden flex flex-col gap-3">
                {
                    rows.map((row, idx) => (
                        <li key={rowKey(row, idx)} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 font-bold text-gray-900 break-words">{titleColumn.render(row, idx)}</div>
                                {
                                    asideColumn ?
                                        <div className="shrink-0">{asideColumn.render(row, idx)}</div>
                                        : null
                                }
                            </div>
                            {
                                fieldColumns.length ?
                                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                                        {
                                            fieldColumns.map((c, i) => (
                                                <div key={i} className="min-w-0">
                                                    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{c.header}</dt>
                                                    <dd className="text-sm text-gray-800 break-words">{c.render(row, idx)}</dd>
                                                </div>
                                            ))
                                        }
                                    </dl>
                                    : null
                            }
                            {
                                actions ?
                                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2 [&>*]:flex-1 [&>*]:min-w-[7rem]">{actions(row, idx)}</div>
                                    : null
                            }
                        </li>
                    ))
                }
            </ul>
        </div>
    )
}
